import { useEffect, useMemo } from 'react';

import classNames from 'classnames';
import { getArchiveId, useIsEditor } from 'modules/archive';
import {
    fetchData,
    getCurrentInterview,
    getCurrentIntervieweeId,
    getTranscriptFetched,
} from 'modules/data';
import { HelpText } from 'modules/help-text';
import { isRtlLanguage, useI18n } from 'modules/i18n';
import { getAutoScroll } from 'modules/interview';
import { getCurrentTape, getIsIdle } from 'modules/media-player';
import { useInterviewContributors } from 'modules/person';
import { usePathBase } from 'modules/routes';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import {
    BookmarkSegmentModal,
    EditableSegment,
    TranscriptSkeleton,
} from './components';
import {
    useProcessedSegments,
    useSegmentEditing,
    useTranscriptBookmarks,
} from './hooks';
import { getContributorInformation, getSegmentTimecodeBounds } from './utils';

/**
 * Loads interview segments and displays the selected transcript with editing and bookmarks.
 *
 * @param {Object} props - Transcript display options.
 * @param {string} [props.transcriptLocale] - Content language code.
 * @param {boolean} [props.originalLocale=false] - Uses the original-transcript empty message.
 * @param {boolean} [props.loadSegments] - Requests segments when not already loaded.
 * @returns {React.ReactElement|string} Transcript, loading skeleton, or empty-language message.
 */
export default function Transcript({
    transcriptLocale,
    originalLocale = false,
    loadSegments,
}) {
    const dispatch = useDispatch();
    const { t } = useI18n();
    const pathBase = usePathBase();
    const isEditviewActive = useIsEditor();

    // Redux state
    const archiveId = useSelector(getArchiveId);
    const interview = useSelector(getCurrentInterview);
    const intervieweeId = useSelector(getCurrentIntervieweeId);
    const tape = useSelector(getCurrentTape);
    const autoScroll = useSelector(getAutoScroll);
    const isIdle = useSelector(getIsIdle);
    const transcriptFetched = useSelector(getTranscriptFetched);

    const { data: people, isLoading: peopleAreLoading } =
        useInterviewContributors(interview?.id);
    const hasTranscript =
        interview?.alpha3s_with_transcript?.indexOf(transcriptLocale) > -1;
    const {
        bookmarkedSegmentIds,
        selectedBookmarkSegment,
        handleBookmarkCreate,
        handleBookmarkModalClose,
    } = useTranscriptBookmarks();

    const segmentEditing = useSegmentEditing();
    const { editingSegmentId } = segmentEditing;

    const contributorInformation = useMemo(
        () => getContributorInformation(interview?.contributions, people),
        [interview?.contributions, people]
    );

    useEffect(() => {
        // Only scroll to top if media has not started yet and auto scroll is off.
        // Otherwise, scrolling is handled in Segment component.
        if (!autoScroll && isIdle) {
            window.scrollTo(0, 0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (loadSegments && !transcriptFetched) {
            dispatch(
                fetchData({ pathBase }, 'interviews', archiveId, 'segments')
            );
        }
    }, [loadSegments, transcriptFetched, archiveId, pathBase, dispatch]);

    const processedSegments = useProcessedSegments(
        interview,
        tape,
        intervieweeId
    );

    if (!interview || !transcriptFetched || peopleAreLoading) {
        return <TranscriptSkeleton count={5} />;
    }

    if (!hasTranscript) {
        return originalLocale
            ? t('without_transcript')
            : t('without_translation');
    }

    return (
        <>
            {isEditviewActive && (
                <HelpText code="interview_transcript" className="u-mb" />
            )}
            <div
                className={classNames('Transcript', {
                    'Transcript--rtl': isRtlLanguage(transcriptLocale),
                })}
                data-testid="transcript"
            >
                {processedSegments.map((segment, index, array) => {
                    const prevSegment = array[index - 1];
                    const nextSegment = array[index + 1];
                    const { prevSegmentTimecode, nextSegmentTimecode } =
                        getSegmentTimecodeBounds(
                            segment,
                            prevSegment,
                            nextSegment
                        );
                    const isEditingSegment =
                        isEditviewActive && editingSegmentId === segment.id;

                    return (
                        <EditableSegment
                            key={`editable-segment-${segment.id}`}
                            segment={segment}
                            interview={interview}
                            contributor={
                                contributorInformation[segment.speaker_id]
                            }
                            contentLocale={transcriptLocale}
                            nextSegmentTime={nextSegment?.time}
                            nextSegmentTape={nextSegment?.tape_nbr}
                            isEditingSegment={isEditingSegment}
                            segmentEditing={segmentEditing}
                            prevSegmentTimecode={prevSegmentTimecode}
                            nextSegmentTimecode={nextSegmentTimecode}
                            hasBookmarks={bookmarkedSegmentIds.has(segment.id)}
                            onBookmarkCreate={handleBookmarkCreate}
                        />
                    );
                })}
            </div>
            {selectedBookmarkSegment && (
                <BookmarkSegmentModal
                    segment={selectedBookmarkSegment}
                    hideButton
                    showDialogInitially
                    onClose={handleBookmarkModalClose}
                />
            )}
        </>
    );
}

Transcript.propTypes = {
    originalLocale: PropTypes.bool,
    loadSegments: PropTypes.bool,
    transcriptLocale: PropTypes.string,
};
